import { Beech } from './beech';
import * as AgentProtocol from './agent-protocol';
import { InviteSelection } from './server';
import CallInfoCmd = AgentProtocol.CallInfoCmd;
import ConnectionData = AgentProtocol.ConnectionData;
import { Call } from '@twilio/voice-sdk';

const DIAL_SERVER_URL = 'http://localhost:9321';

export default class Flow {
	private beech: Beech;
	private wrapupTimer: number | null = null;

	private static readonly DEVICE_RECOVERY_COOLDOWN_MS = 2 * 60 * 1000;
	private deviceRecoveryPending = false;

	constructor(beech: Beech) {
		this.beech = beech;

		this.beech.on('ready', this.init);
	}

	private init = () => {
		logg.log('flow >> init');

		this.beech.model.setAgentFlow('CONNECTING');
		this.beech.foreman.register();
		this.beech.notify.register();
		this.beech.server.connect();
	};

	public onServerHello = () => {
		logg.log('flow >> onServerHello');

		this.beech.model.setHeadsetState('IDLE');
		this.beech.model.setAgentFlow('SETUP');
		this.beech.setup.init();
	};

	public onSetupComplete = () => {
		logg.log('flow >> onSetupComplete');

		this.beech.model.setAgentFlow('LOGIN');
	};

	public doLoginPassword = (email: string, password: string) => {
		logg.log(`flow >> doLoginPassword(${email})`);

		this.beech.model.setLoginError('');
		this.beech.server.doLoginPassword(email, password);
	};

	public doLoginSession = (email: string, session: string) => {
		logg.log(`flow >> doLoginSession(${email})`);

		this.beech.model.setLoginError('');
		this.beech.server.doLoginSession(email, session);
	};

	public onLoginError = (msg: string) => {
		logg.log(`flow >> onLoginError(${msg})`);

		// Surface the error on the login screen. Covers a resume whose saved session
		// was rejected (e.g. past the 30-day cap): the token's been cleared, so fall
		// back to a normal password login rather than stranding on a blank screen.
		this.beech.model.setLoginError(msg || 'Login Failed');
		this.beech.model.setAgentFlow('LOGIN');
	};

	// 13h session timeout. We're signed out of the live roster but still hold a
	// valid session token, so offer a one-click resume instead of forcing a login.
	public onSessionTimedOut = () => {
		logg.log('flow >> onSessionTimedOut');

		this.beech.model.setAgentFlow('TIMED_OUT');
	};

	public onTwilioDeviceLost = (reason: string) => {
		const flow = this.beech.model.getAgentFlow();

		if (flow === 'RINGING' || flow === 'TALKING' || flow === 'WRAPUP') {
			logg.warn(`flow >> twilio device lost (${reason}) during ${flow}; deferring to idle`);
			this.deviceRecoveryPending = true;
			return;
		}

		if (flow !== 'IDLE') {
			logg.warn(`flow >> twilio device lost (${reason}) while ${flow}; not recovering`);
			return;
		}

		const lastRecovery = this.beech.persist.getLastDeviceRecoveryAt();
		if (Date.now() - lastRecovery < Flow.DEVICE_RECOVERY_COOLDOWN_MS) {
			logg.error(`flow >> twilio device lost (${reason}) again after recovery; giving up`);
			this.beech.server.sendClientLog('device-recovery-failed', reason);
			this.beech.model.addAlert(
				'Aura Connect keeps losing the call service. Please restart the app, or check your microphone/headset.'
			);
			return;
		}

		logg.warn(`flow >> twilio device lost (${reason}); recovering via reload`);
		this.beech.persist.setLastDeviceRecoveryAt(Date.now());

		if (this.beech.embedded.isEnabled()) {
			this.beech.embedded.doReload();
		} else {
			window.location.reload();
		}
	};

	private recoverDeviceIfPending = () => {
		if (!this.deviceRecoveryPending) {
			return;
		}
		this.deviceRecoveryPending = false;
		if (this.beech.twilio.isRegistered()) {
			return;
		}
		this.onTwilioDeviceLost('post-call');
	};

	// Deliberate sign-out from settings. Revokes the session server-side and
	// returns to a clean login screen.
	public logout = () => {
		logg.log('flow >> logout');

		this.beech.model.setLoginError('');
		this.beech.model.setAgentFlow('CONNECTING');
		void this.beech.server.signOut();
	};

	// "Resume" — reconnect and silently re-auth via the saved session token.
	public resumeSession = () => {
		logg.log('flow >> resumeSession');

		this.beech.model.setLoginError('');
		this.beech.model.setAgentFlow('CONNECTING');
		this.beech.server.reconnectNow();
	};

	// "Log in as someone else" — discard the saved session (e.g. shift handover on a
	// shared workstation), then reconnect into the normal login flow.
	public discardSession = async () => {
		logg.log('flow >> discardSession');

		await this.beech.persist.clearSessionToken();
		this.beech.model.setLoginError('');
		this.beech.model.setAgentFlow('CONNECTING');
		this.beech.server.reconnectNow();
	};

	public onServerReady = async (data: AgentProtocol.RefreshCmd) => {
		logg.log('flow >> onServerReady');

		this.beech.model.setUsername(data.username);
		this.beech.model.setRules(data.rules);
		this.beech.model.loadPersistedUserState();

		// Load the contacts directory up front so call screens (recent calls,
		// incoming/live calls) can resolve numbers to contact names, not just
		// the Contacts tab.
		this.beech.server.listContacts();

		await this.beech.twilio.setup(data.twilioToken);

		// reconnect
		if (this.beech.twilio.isReady()) {
			const flow = this.beech.model.getAgentFlow();
			if (flow === 'LOGIN' || flow === 'CONNECTING' || flow === 'SETUP') {
				this.beech.audio.playLogin();
				this.beech.model.setAgentFlow('IDLE');
				this.recoverDeviceIfPending();
			}
		}

		// first login
		else {
			this.beech.audio.playLogin();
		}
	};

	public onTwilioReady = () => {
		logg.log('flow >> onTwilioReady');
		this.beech.model.setAgentFlow('IDLE');
	};

	public onTwilioIncoming = (connection: Call) => {
		logg.log('flow >> onTwilioIncoming');

		this.beech.notify.showIncomingCallNotification();
		this.beech.embedded.onCallIncoming();
		this.beech.model.setCallLocalSid(connection.parameters.CallSid);
		this.beech.model.setAgentFlow('RINGING');

		window.electronAPI?.showWindow();
	};

	public doOutgoingDial = async (number: string) => {
		logg.log(`flow >> doOutgoingDial(${number})`);

		if (this.beech.model.getAgentFlow() != 'IDLE') {
			throw new Error(`Can only dial when IDLE; currently ${this.beech.model.getAgentFlow()}`);
		}

		let call = await this.beech.twilio.doOutgoingDial(number);
		call.on('accept', () => {
			this.beech.model.setCallLocalSid(call.parameters.CallSid);
		});

		this.beech.model.setCallDirection('out');
		this.beech.model.setAgentFlow('TALKING');
		this.beech.model.clearTalkingDigits();
		this.beech.model.startCallTimer();
		this.beech.model.setCallHolding(false);
	};

	public addCallInfo = (info: CallInfoCmd) => {
		logg.log('flow >> addCallInfo');

		this.beech.model.addCallInfo(info);
		this.beech.notify.refreshIncomingCallNotification();
	};

	public doRingingAccept = () => {
		logg.log('flow >> doRingingAccept');

		this.beech.twilio.doAccept();
		this.beech.model.setCallDirection('in');
		this.beech.model.setAgentFlow('TALKING');
		this.beech.model.clearTalkingDigits();
		this.beech.model.startCallTimer();
		this.beech.model.setCallHolding(false);
		this.beech.notify.hideIncomingCallNotification();

		const callRemoteUsers = this.beech.model.getData().callRemoteUsers || [];
		const firstResidentId = callRemoteUsers.find((user) => user.residentId !== undefined)?.residentId;
		this.beech.embedded.onCallAccept(firstResidentId);

		if (firstResidentId) {
			this.notifyWisteria('onCallAccept', { residentId: firstResidentId });
		}
	};

	public doRingingReject = () => {
		logg.log('flow >> doRingingReject');

		this.beech.twilio.doReject();
		this.beech.model.setAgentFlow('IDLE');
		this.beech.model.setCallLocalSid(null);
		this.beech.model.clearCallTimer();
		this.beech.notify.hideIncomingCallNotification();
		this.beech.embedded.onCallReject();
		this.recoverDeviceIfPending();
	};

	public onTwilioConnect = () => {
		logg.log('flow >> onTwilioConnect');

		this.beech.twilio.handleMute();
		this.beech.notify.hideIncomingCallNotification();

		const callRemoteUsers = this.beech.model.getData().callRemoteUsers || [];
		const firstResidentId = callRemoteUsers.find((user) => user.residentId !== undefined)?.residentId;
		this.beech.embedded.onCallConnected(firstResidentId);

		if (firstResidentId) {
			this.notifyWisteria('onCallConnected', { residentId: firstResidentId });
		}
	};

	public onTwilioDisconnect = () => {
		logg.log('flow >> onTwilioDisconnect');

		// Snapshot call data for recent calls before clearing
		const data = this.beech.model.getData();
		if (data.callRemoteNumber && data.callStartMillis) {
			const remoteUsers = data.callRemoteUsers || [];
			const firstUser = remoteUsers.length > 0 ? remoteUsers[0] : null;
			const name = firstUser ? `${firstUser.firstname} ${firstUser.lastname}`.trim() : null;

			this.beech.model.addRecentCall({
				number: data.callRemoteNumber,
				name: name || null,
				duration: Date.now() - data.callStartMillis,
				timestamp: Date.now(),
				direction: data.callDirection || 'in'
			});
		}

		this.beech.server.cancelSimulatedDrop();

		this.doWrapup();
		this.beech.model.clearCallQualityWarnings();
		this.beech.model.setCallLocalSid(null);
		this.beech.model.clearCallTimer();
		this.beech.model.setAgentMuted(false);
		// Close the invite drawer and clear its roster when the call ends, so the next call can't
		// briefly show the previous call's invite targets.
		this.beech.model.setInvitePopoverVisible(false);
		this.beech.notify.hideIncomingCallNotification();
		this.beech.embedded.onCallDisconnect();

		this.notifyWisteria('onCallDisconnect');
	};

	public doTalkingHangup = () => {
		logg.log('flow >> doTalkingHangup');

		this.beech.twilio.doHangup();
	};

	public doCleanShutdown = () => {
		const flow = this.beech.model.getAgentFlow();
		logg.log(`flow >> doCleanShutdown >> ${flow}`);

		if (flow === 'RINGING') {
			this.doRingingReject();
		} else if (flow === 'TALKING') {
			this.doTalkingHangup();
		}
	};

	public doTalkingHold = () => {
		logg.log('flow >> doTalkingHold');

		let callSid = this.beech.model.getCallLocalSid();
		if (callSid == null) {
			throw Error('doTalkingHold >> No active call');
		}

		this.beech.server.modifyCallHold(callSid, true);
	};

	public doTalkingUnhold = () => {
		logg.log('flow >> doTalkingUnhold');

		let callSid = this.beech.model.getCallLocalSid();
		if (callSid == null) {
			throw Error('doTalkingUnhold >> No active call');
		}

		this.beech.server.modifyCallHold(callSid, false);
	};

	public doTalkingInvite = (target: InviteSelection) => {
		logg.log('flow >> doTalkingInvite');

		this.beech.model.setInvitePopoverVisible(false);
		this.beech.server.doSync();

		let callSid = this.beech.model.getCallLocalSid();
		if (callSid == null) {
			throw Error('doTalkingInvite >> No active call');
		}

		if (target.guid) {
			this.beech.server.inviteAgentToCall(callSid, target.guid);
		} else if (target.grandma) {
			let grandmaId = parseInt(target.grandma);
			if (isNaN(grandmaId)) {
				this.beech.model.addAlert('Invalid Grandma ID');
			} else {
				this.beech.server.inviteGrandmaToCall(callSid, grandmaId);
			}
		} else if (target.number) {
			this.beech.server.inviteExternalToCall(callSid, {
				name: target.name ?? null,
				number: target.number,
				digits: target.digits ?? null
			});
		} else {
			throw new Error('flow >> doTalkingInvite: invalid selection');
		}
	};

	public doTalkingConnectionKick = (connection: ConnectionData) => {
		logg.log('flow >> doTalkingConnectionKick');

		let callSid = this.beech.model.getCallLocalSid();
		if (callSid == null) {
			throw Error('doTalkingKick >> No active call');
		}

		this.beech.server.doKickFromCall(callSid, connection.guid);
	};

	public doTalkingToggleTranslation = () => {
		logg.log('flow >> doTalkingToggleTranslation');

		let callSid = this.beech.model.getCallLocalSid();
		if (callSid == null) {
			throw Error('doTalkingToggleTranslation >> No active call');
		}

		this.beech.server.toggleTranslation(callSid);
	};

	public doTalkingConnectionMute = (connection: ConnectionData) => {
		logg.log('flow >> doTalkingConnectionMute');

		let callSid = this.beech.model.getCallLocalSid();
		if (callSid == null) {
			throw Error('doTalkingConnectionMute >> No active call');
		}

		this.beech.server.modifyCallMute(callSid, connection.guid, true);
	};

	public doTalkingConnectionUnmute = (connection: ConnectionData) => {
		logg.log('flow >> doTalkingConnectionUnmute');

		let callSid = this.beech.model.getCallLocalSid();
		if (callSid == null) {
			throw Error('doTalkingConnectionUnmute >> No active call');
		}

		this.beech.server.modifyCallMute(callSid, connection.guid, false);
	};

	public doWrapupContinue = () => {
		logg.log('flow >> doWrapupContinue');

		this.beech.model.setAgentFlow('IDLE');
		this.beech.model.clearWrapupCountdown();
		this.updateWrapupTimer();
		this.recoverDeviceIfPending();
	};

	public doWrapupExtend = (seconds: number) => {
		logg.log(`flow >> doWrapupExtend seconds=${seconds}`);

		this.beech.model.extendWrapupCountdown(seconds * 1000);
		this.updateWrapupTimer();
	};

	public doWrapupStop = () => {
		logg.log('flow >> doWrapupStop');

		this.beech.model.setAgentFlow('IDLE');
		this.beech.model.setHeadsetState('IDLE');
		this.beech.model.clearWrapupCountdown();
		this.updateWrapupTimer();
		this.recoverDeviceIfPending();
	};

	public toggleInvitePopover = () => {
		logg.log('flow >> toggleInvitePopover');

		this.beech.model.toggleInvitePopover();
		this.beech.server.doSync();
	};

	private doWrapup = () => {
		logg.log('flow >> doWrapup');

		const headsetState = this.beech.model.getHeadsetState();
		if (headsetState === 'AVAILABLE') {
			this.beech.model.setAgentFlow('WRAPUP');
			this.beech.model.startWrapupCountdown(10900); // 10.9 seconds
			this.updateWrapupTimer();
		} else {
			this.beech.model.setAgentFlow('IDLE');
			this.beech.model.clearWrapupCountdown();
			this.updateWrapupTimer();
			this.recoverDeviceIfPending();
		}
	};

	private notifyWisteria = (type: string, data: Record<string, any> = {}) => {
		if (!window.electronAPI?.isElectron) return;
		fetch(`${DIAL_SERVER_URL}/event`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ type, data })
		}).catch((err) => logg.warn(`flow >> notifyWisteria(${type}) failed`, err));
	};

	private updateWrapupTimer = () => {
		if (this.wrapupTimer) {
			window.clearTimeout(this.wrapupTimer);
			this.wrapupTimer = null;
		}

		let wrapupMillis = this.beech.model.getWrapupEndMillis();
		if (wrapupMillis) {
			let millisRemaining = wrapupMillis - Date.now();
			this.wrapupTimer = window.setTimeout(() => this.doWrapupContinue(), millisRemaining);
		}
	};
}
